import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import React from 'react';

import {REDESIGN_DRAWER_WIDTH_CSS_VAR} from 'llm/chat-library/ui/AIStyleConstants';

/** Transparent grab area centered on the drawer's right edge; wider than the visible rule for easy grabbing. */
const HANDLE_HIT_WIDTH_PX = 9;

const useStyles = createStyles(() => ({
  handle: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    // Track the drawer's right edge, which is driven by the width CSS variable.
    left: `var(${REDESIGN_DRAWER_WIDTH_CSS_VAR})`,
    width: HANDLE_HIT_WIDTH_PX,
    transform: `translateX(-${HANDLE_HIT_WIDTH_PX / 2}px)`,
    cursor: 'col-resize',
    zIndex: 6,
    // A 2px rule centered in the hit area. Transparent at rest (the drawer already draws a border);
    // it reveals on hover and turns blue while dragging, matching SidebarResizeSeparator's tokens.
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 2,
      backgroundColor: 'transparent',
      transition: 'background-color 120ms ease-in-out',
    },
    '&:hover::after': {
      backgroundColor: Color.BLUE_100,
    },
  },
  handleActive: {
    '&::after': {
      backgroundColor: Color.BLUE_150,
    },
    '&:hover::after': {
      backgroundColor: Color.BLUE_150,
    },
  },
}));

export interface DrawerResizeHandleProps {
  isResizing: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
}

/**
 * Draggable divider on the right edge of the redesigned chat left drawer. Purely visual + a11y
 * affordance; drag state lives in `useResizableDrawer`.
 */
export default function DrawerResizeHandle({isResizing, onMouseDown}: DrawerResizeHandleProps): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      className={cx(classes.handle, isResizing && classes.handleActive)}
      onMouseDown={onMouseDown}
      {...getTestProps({component: 'AISidepanel', element: 'drawer-resize-handle'})}
    />
  );
}
